import { defineStore } from "pinia";
import { ref, computed } from "vue";
import type { FetchTicketResponsePriority, NestedTicketStatus } from "src/api/models";
import { useTicketService } from "src/services/ticket-service";

export const useTrelloBoardStore = defineStore(
  "trello-board",
  () => {
    const ticketService = useTicketService();

    const searchInput = ref("");
    const selectedStatusSort = ref<string[]>([]);
    const selectedPrioritySort = ref<FetchTicketResponsePriority[]>([]);
    const selectedAssignees = ref<string[]>([]);
    const selectedAssigneeName = ref("");
    const selectedUnits = ref<string[]>([]);
    const selectedUnitName = ref("");
    const startDate = ref(new Date());
    const ticketContainerWidth = ref(200);
    const isWeekendVisible = ref(true);
    const singleLaneScroll = ref(false);
    const ticketStatuses = ref<NestedTicketStatus[]>([]);

    async function fetchAllTicketStatuses() {
      const data = await ticketService.fetchAllTicketStatuses();
      ticketStatuses.value = data ?? [];
    }

    function clearFilters() {
      searchInput.value = "";
      selectedStatusSort.value = [];
      selectedPrioritySort.value = [];
      selectedAssignees.value = [];
      selectedAssigneeName.value = "";
      selectedUnits.value = [];
      selectedUnitName.value = "";
      singleLaneScroll.value = false;
      startDate.value = new Date();
    }

    /** Set of status IDs that should not appear on the board. */
    const hiddenStatusIds = computed(() => {
      const ids = new Set<string>();
      for (const s of ticketStatuses.value) {
        if (s.visible_on_board === false && s.id) {
          ids.add(s.id);
        }
      }
      return ids;
    });

    return {
      searchInput,
      selectedStatusSort,
      selectedPrioritySort,
      selectedAssignees,
      selectedAssigneeName,
      selectedUnits,
      selectedUnitName,
      startDate,
      ticketContainerWidth,
      isWeekendVisible,
      singleLaneScroll,
      clearFilters,
      fetchAllTicketStatuses,
      ticketStatuses,
      hiddenStatusIds,
    };
  },
  {
    persist: {
      storage: localStorage,
      paths: [
        "searchInput",
        "selectedStatusSort",
        "selectedPrioritySort",
        "selectedAssignees",
        "selectedAssigneeName",
        "selectedUnits",
        "selectedUnitName",
        "startDate",
        "ticketContainerWidth",
        "isWeekendVisible",
        "singleLaneScroll",
      ],
      afterRestore: (ctx) => {
        // JSON.parse deserializes Dates as strings — convert back
        if (typeof ctx.store.startDate === "string") {
          ctx.store.startDate = new Date(ctx.store.startDate);
        }
      },
    },
  }
);
